import { api } from 'src/api';

// Direct API calls, not a store model's run(): run() cancels its own in-flight call, and the setup page shows a section
// per utility. Kept here, as the company's plan is (company/subscriptionTierRead).
export const NO_OPTIONS = {
  cancelToken: {}
};

/**
 * Said after a link, a fork, a cancelled move or a setup edit: they change more than one setup's counts, so every section
 * reads again.
 */
export const UTILITY_SETUP_CHANGED = 'livable:utility-setup-changed';

// How long a read is kept. A setup made elsewhere -- another tab, the setups list -- shows on the first read after
// that, with no reload.
const KEPT_MS = 60000;
const reads = new Map();

/**
 * The company's setups of one utility type, or null when they can't be read. One request serves every section on the
 * page that asks for the same type; a failed read isn't kept, so the next section asks again.
 */
export const readCompanySetups = (companyId, utilityTypeId) => {
  const key = "".concat(companyId, "|").concat(String(utilityTypeId).toLowerCase());
  const kept = reads.get(key);
  if (kept && Date.now() - kept.at < KEPT_MS) return kept.read;
  const read = api.UtilitySetups.getCompanySetups({
    companyId,
    utilityTypeId
  }, NO_OPTIONS).then(resp => Array.isArray(resp === null || resp === void 0 ? void 0 : resp.response) ? resp.response : null).catch(() => null);
  reads.set(key, {
    read,
    at: Date.now()
  });
  read.then(setups => {
    var _reads$get;
    if (setups === null && ((_reads$get = reads.get(key)) === null || _reads$get === void 0 ? void 0 : _reads$get.read) === read) reads.delete(key);
  });
  return read;
};

/**
 * A setup's history (LB-6180), or null when it can't be read. One request serves every section on the page that follows
 * the setup, kept as the company's setups are, for the company it was read under; any setup change drops it.
 */
export const readSetupHistory = (companyId, setupId) => {
  const key = "history|".concat(companyId, "|").concat(String(setupId).toLowerCase());
  const kept = reads.get(key);
  if (kept && Date.now() - kept.at < KEPT_MS) return kept.read;
  const read = api.UtilitySetups.getSetupHistory({
    setupId
  }, NO_OPTIONS).then(resp => {
    var _resp$response;
    return Array.isArray(resp === null || resp === void 0 || (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.entries) ? resp.response : null;
  }).catch(() => null);
  reads.set(key, {
    read,
    at: Date.now()
  });
  read.then(history => {
    var _reads$get2;
    if (history === null && ((_reads$get2 = reads.get(key)) === null || _reads$get2 === void 0 ? void 0 : _reads$get2.read) === read) reads.delete(key);
  });
  return read;
};

/**
 * Drops every kept read without telling the sections: on logout, as the plans and intake addresses are, since a history
 * answers the caller who read it (property names in its outcomes are left out for one who can't see them).
 */
export const forgetSetupReads = () => {
  reads.clear();
};

/**
 * Drops every kept read and tells every section to read again. The section that wrote already shows the write's answer,
 * so it names itself and reads only the company's setups again; with no utility named, every section reads again.
 *
 * `appliedSetupId` names a setup whose new values applied at once (LB-6189): every utility that follows it changed, so
 * each section on it hands those values to its form once it has read them.
 */
export const announceSetupChange = function (propertyUtilityId) {
  let _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$appliedSetupId = _ref.appliedSetupId,
    appliedSetupId = _ref$appliedSetupId === void 0 ? null : _ref$appliedSetupId;
  reads.clear();
  window.dispatchEvent(new CustomEvent(UTILITY_SETUP_CHANGED, {
    detail: {
      propertyUtilityId,
      appliedSetupId
    }
  }));
};